import { useState } from 'react'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import { PillTabs } from '@/components/PillTabs'
import { cn } from '@/lib/utils'
import type { ChallengeSummary } from '@/types/strategies'

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'En curso',
  PAUSED: 'En curso',
  FAILED: 'Fallido',
  COMPLETED: 'Completado',
  RESET_BY_USER: 'Reiniciado',
}

function rangeLabel(challenge: ChallengeSummary): string {
  if (!challenge.week_start_date || !challenge.week_end_date) return challenge.name
  const s = new Date(`${challenge.week_start_date}T12:00:00`).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })
  const e = new Date(`${challenge.week_end_date}T12:00:00`).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })
  return `${s} - ${e} ${challenge.week_start_date.slice(0, 4)}`
}

function HistoryRow({ challenge, onOpen }: { challenge: ChallengeSummary; onOpen: () => void }) {
  const resultAmount = challenge.current_bankroll - challenge.bankroll_initial
  const positive = resultAmount >= 0
  const isActive = challenge.status === 'ACTIVE'
  return (
    <button
      onClick={onOpen}
      className="w-full flex items-center justify-between gap-2 bg-panel-elevated/30 border border-border/30 rounded-xl px-3 py-2.5 text-left active-scale hover:border-border/60 transition"
    >
      <span className="text-xs font-bold text-text">{rangeLabel(challenge)}</span>
      <div className="flex items-center gap-2 shrink-0">
        <span className={cn('text-xs font-mono font-black', positive ? 'text-success' : 'text-danger')}>
          {positive ? '+' : ''}{resultAmount.toFixed(2)}€
        </span>
        <span
          className={cn(
            'text-[9px] px-2 py-0.5 rounded-full border font-bold uppercase',
            isActive ? 'bg-brand/15 text-brand border-brand/40' : 'bg-panel text-text-dim border-border/50',
          )}
        >
          {STATUS_LABEL[challenge.status] ?? challenge.status}
        </span>
        <ChevronRight className="h-3.5 w-3.5 text-text-dim" />
      </div>
    </button>
  )
}

/** P1.9F (25/09/2026, referencia visual aprobada -- "Historial de Retos"): lista de TODAS
 * las semanas (Reto Oficial y Mi Reto, en tabs separadas) -- cada fila abre `WeekView` con
 * el detalle completo real (Sección 3 del encargo: "haz clicables las semanas anteriores").
 * Reutiliza `useChallenges()` ya existente, sin ningún endpoint nuevo. */
export function HistoryView({
  officials, mine, onBack, onOpenWeek,
}: {
  officials: ChallengeSummary[]
  mine: ChallengeSummary[]
  onBack: () => void
  onOpenWeek: (challengeId: number, isOfficial: boolean) => void
}) {
  const [tab, setTab] = useState<'oficial' | 'mio'>('oficial')
  const list = tab === 'oficial' ? officials : mine
  const sorted = [...list].sort((a, b) => (b.week_start_date ?? '').localeCompare(a.week_start_date ?? ''))

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-xl bg-panel-elevated hover:bg-border text-text transition">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <h1 className="text-lg font-black text-text">Historial de Retos</h1>
          <p className="text-[10px] text-text-dim uppercase tracking-wider font-bold">John Anthony</p>
        </div>
      </div>

      <PillTabs
        items={[
          { key: 'oficial', label: 'Reto Oficial', count: officials.length },
          { key: 'mio', label: 'Mi Reto', count: mine.length },
        ]}
        active={tab}
        onChange={(key) => setTab(key as 'oficial' | 'mio')}
      />

      {sorted.length === 0 ? (
        <p className="text-xs text-text-dim text-center py-8">Todavía no hay semanas registradas aquí.</p>
      ) : (
        <div className="space-y-1.5">
          {sorted.map((c) => (
            <HistoryRow key={c.challenge_id} challenge={c} onOpen={() => onOpenWeek(c.challenge_id, tab === 'oficial')} />
          ))}
        </div>
      )}
    </div>
  )
}
